import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import ViewTestDataCSS from "./ViewTestData.module.css";
interface DataSectionComponentProps {
  jsonObject: Record<string, any>;
}

const DynamicAccordion: React.FC<{
  title: string;
  expanded: boolean;
  onChange: () => void;
  children: React.ReactNode;
}> = ({ title, expanded, onChange, children }) => (
  <Box
    className={`{ViewTestDataCSS.accordionHeader} ${
      expanded ? "expanded" : ""
    }`}
  >
    <Box
      className={ViewTestDataCSS.accordionHeader}
      onClick={onChange}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px",
        cursor: "pointer",
      }}
    >
      <Box sx={{ width: "25%" }}> {title}</Box>
      <Tooltip
        title={expanded ? "Collapse" : "Expand"}
        placement="right"
      >
        <IconButton
          size="large"
          sx={{ marginLeft: "30px" }}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Tooltip>
    </Box>
    {expanded && <Box>{children}</Box>}
  </Box>
);

const TopKeyValuePair: React.FC<{
  keyText: string;
  value: any;
  isEven: boolean;
}> = ({ keyText, value, isEven }) => (
  <Box
    sx={{
      display: "flex",
      backgroundColor: isEven ? "white" : "#f4c5c330",
      padding: "8px",
      borderRadius: "5px",
      flex: 1,
    }}
  >
    <Box className={ViewTestDataCSS.keyLabel}>{keyText}</Box>
    <Box className={ViewTestDataCSS.valueContent}>
      {value != null ? value.toString() : "N/A"}
    </Box>
  </Box>
);

const DataSectionComponentTrail: React.FC<DataSectionComponentProps> = ({
  jsonObject,
}) => {
  const [expandedState, setExpandedState] = useState<Record<string, boolean>>(
    {}
  );

  const toggleExpandCollapse = (expandAll: boolean) => {
    const updateStateRecursively = (obj: any, path: string = "") => {
      Object.keys(obj).forEach((key) => {
        const currentPath = path ? `${path}.${key}` : key;
        expandedState[currentPath] = expandAll;

        if (typeof obj[key] === "object" && obj[key] !== null) {
          updateStateRecursively(obj[key], currentPath);
        }
      });
    };

    updateStateRecursively(jsonObject);
    setExpandedState({ ...expandedState });
  };

  const handleToggle = (path: string) => {
    setExpandedState((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const renderData = (
    data: any,
    key: string,
    index: number,
    path: string = ""
  ): React.ReactNode => {
    const currentPath = path ? `${path}.${key}` : key;
    const isExpanded = expandedState[currentPath] ?? false;

    if (data === null || data === undefined) {
      return (
        <TopKeyValuePair
          key={currentPath}
          keyText={key}
          value="N/A"
          isEven={index % 2 === 0}
        />
      );
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      return (
        <DynamicAccordion
          key={currentPath}
          title={key}
          expanded={isExpanded}
          onChange={() => handleToggle(currentPath)}
        >
          <Box className={ViewTestDataCSS.stepAccordion}>
            {Object.entries(data).map(([subKey, subValue], index) => (
              <React.Fragment key={`${currentPath}-${subKey}`}>
                {renderData(subValue, subKey, index, currentPath)}
              </React.Fragment>
            ))}
          </Box>
        </DynamicAccordion>
      );
    } else if (Array.isArray(data)) {
      return (
        <DynamicAccordion
          key={currentPath}
          title={key}
          expanded={isExpanded}
          onChange={() => handleToggle(currentPath)}
        >
          <Box className={ViewTestDataCSS.stepAccordion}>
            {data.length === 0 ? (
              <Typography>No data available</Typography>
            ) : (
              data.map((item, idx) =>
                typeof item === "object" ? (
                  <Box key={`${currentPath}.${idx}`}>
                    {Object.entries(item).map(([subKey, subValue], index) => (
                      <React.Fragment key={`${currentPath}-${subKey}-${index}`}>
                        {renderData(subValue, subKey, index, currentPath)}
                      </React.Fragment>
                    ))}
                  </Box>
                ) : (
                  <TopKeyValuePair
                    key={`${currentPath}.${idx}`}
                    keyText={`Item ${idx}`}
                    value={item}
                    isEven={idx % 2 === 0}
                  />
                )
              )
            )}
          </Box>
        </DynamicAccordion>
      );
    } else {
      return (
        <TopKeyValuePair
          key={`topkeypair-${key}-${index}`}
          keyText={key}
          value={data}
          isEven={index % 2 === 0}
        />
      );
    }
  };

  return (
    <>
      <Box className={ViewTestDataCSS.expandAllBox}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => toggleExpandCollapse(true)}
        >
          Expand All
        </Button>

        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => toggleExpandCollapse(false)}
        >
          Collapse All
        </Button>
      </Box>
      <Box className={ViewTestDataCSS.accordion}>
        <Box
          sx={{ overflow: "scroll", height: "60vh", padding: "0rem 1rem 1rem" }}
        >
          {Object.keys(jsonObject).length > 0 && (
            <Box>
              {Object.keys(jsonObject).map((key, index) => {
                const value = jsonObject[key];

                if (typeof value !== "object" && value !== null) {
                  return (
                    <Box
                      key={`toplevel-${key}-${index}`}
                      className={`${ViewTestDataCSS.toplevelKeys} ${
                        index % 2 === 0
                          ? ViewTestDataCSS.even
                          : ViewTestDataCSS.odd
                      }`}
                    >
                      <TopKeyValuePair
                        key={`topkeypair-${key}-${index}`}
                        keyText={key}
                        value={value}
                        isEven={index % 2 === 0}
                      />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={`nestedbox-${key}-${index}`}
                      paddingTop="10px"
                    >
                      {renderData(value, key, index)}
                    </Box>
                  );
                }
              })}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default DataSectionComponentTrail;
